import { useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import apiService from '../../services/api'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import {
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineEnvelope,
  HiOutlineUsers,
  HiOutlineCheckCircle,
} from 'react-icons/hi2'
import {
  Banner,
  Button,
  Card,
  FormField,
  IconButton,
  Input,
  Select,
  Textarea,
} from '../../components/ui'

const invitationSchema = z.object({
  invitations: z.array(z.object({
    email: z.string().email('Invalid email format'),
    name: z.string().min(1, 'Name is required'),
  })).min(1, 'At least one invitation is required'),
  assigned_role: z.enum(['admin', 'diamond', 'gold', 'silver']),
  message: z.string().max(500, 'Message must be under 500 characters').optional(),
})

export default function InviteEmployees({ onClose }) {
  const { t } = useTranslation()
  const { showSuccess, showError } = useAlerts()
  const { getEntity } = useWorkspace()
  const [isLoading, setIsLoading] = useState(false)
  const [invitationsSent, setInvitationsSent] = useState([])

  const employeeTerm = getEntity('employee')
  const connectionTerm = getEntity('connection')
  const caseTerm = getEntity('case')
  const documentTerm = getEntity('document')
  const sessionTerm = getEntity('session')

  const {
    register, control, handleSubmit, formState: { errors }, reset, watch,
  } = useForm({
    resolver: zodResolver(invitationSchema),
    defaultValues: {
      invitations: [{ email: '', name: '' }],
      assigned_role: 'silver',
      message: '',
    },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'invitations' })
  const watchedRole = watch('assigned_role')

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)
      const payload = {
        emails: data.invitations.map(inv => inv.email),
        invited_names: data.invitations.map(inv => inv.name),
        assigned_role: data.assigned_role,
        message: data.message || undefined,
      }
      const response = await apiService.post('/send-invitation/', payload)
      if (response.success) {
        setInvitationsSent(response.data.data?.invitations_sent || [])
        showSuccess(t('connections.invite.successCount', {
          count: data.invitations.length,
          role: data.assigned_role,
        }))
        reset()
        if (onClose) onClose()
      } else {
        // TODO: needs i18n key for "Failed to send invitations" error fallback
        showError(response.error?.message || 'Failed to send invitations')
      }
    } catch {
      showError(t('common.networkError'))
    } finally {
      setIsLoading(false)
    }
  }

  // TODO: needs i18n keys for individual role description help text (admin/diamond/gold/silver)
  const getRoleDescription = (role) => {
    switch (role) {
      case 'admin':   return 'Full access to all account resources'
      case 'diamond': return `Access to all ${caseTerm.plural.toLowerCase()}, ${documentTerm.plural.toLowerCase()}, and ${sessionTerm.plural.toLowerCase()}`
      case 'gold':    return 'Moderate access with limited write permissions'
      case 'silver':  return 'Basic read-only access to assigned resources'
      default:        return ''
    }
  }

  if (invitationsSent.length > 0) {
    return (
      <Card padding="lg">
        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-success-soft text-success mx-auto mb-4">
            <HiOutlineCheckCircle className="w-9 h-9" />
          </div>
          <h3 className="text-lg font-semibold text-ink-primary mb-2">{t('connections.invite.successTitle')}</h3>
          <p className="text-sm text-ink-secondary mb-5">
            {t('connections.invite.successCount', { count: invitationsSent.length, role: watchedRole })}
          </p>
          <Card variant="bordered" padding="md" className="mb-5 text-start">
            <h4 className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">{t('connections.invite.sentTo')}</h4>
            <ul className="space-y-1">
              {invitationsSent.map((inv) => (
                <li key={inv.id} className="text-sm text-ink-secondary">
                  {inv.invited_name} <span className="text-ink-muted">({inv.invited_email})</span>
                </li>
              ))}
            </ul>
          </Card>
          <Button onClick={() => setInvitationsSent([])}>{t('connections.invite.sendMore')}</Button>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <FormField label={t('connections.invite.assignRole')} help={getRoleDescription(watchedRole)}>
          <Select size="lg" {...register('assigned_role')}>
            <option value="silver">{t('connections.invite.roleSilver')}</option>
            <option value="gold">{t('connections.invite.roleGold')}</option>
            <option value="diamond">{t('connections.invite.roleDiamond')}</option>
            <option value="admin">{t('connections.invite.roleAdmin')}</option>
          </Select>
        </FormField>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-ink-secondary">
              {t('connections.invite.invitationsLabel', { entity: employeeTerm.singular })}
            </span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leadingIcon={HiOutlinePlus}
              onClick={() => fields.length < 10 && append({ email: '', name: '' })}
              disabled={fields.length >= 10}
            >
              {t('connections.invite.addAnother')}
            </Button>
          </div>

          <div className="space-y-2">
            {fields.map((field, index) => (
              <div key={field.id} className="flex gap-2">
                <div className="flex-1">
                  <Input
                    placeholder={t('connections.invite.namePlaceholder', { entity: employeeTerm.singular })}
                    invalid={!!errors.invitations?.[index]?.name}
                    {...register(`invitations.${index}.name`)}
                  />
                  {errors.invitations?.[index]?.name && (
                    <p className="text-xs text-danger mt-1">{errors.invitations[index].name.message}</p>
                  )}
                </div>
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder={t('connections.invite.emailPlaceholder')}
                    leadingIcon={HiOutlineEnvelope}
                    invalid={!!errors.invitations?.[index]?.email}
                    {...register(`invitations.${index}.email`)}
                  />
                  {errors.invitations?.[index]?.email && (
                    <p className="text-xs text-danger mt-1">{errors.invitations[index].email.message}</p>
                  )}
                </div>
                {fields.length > 1 && (
                  <IconButton
                    icon={HiOutlineTrash}
                    /* TODO: needs i18n key for "Remove invitation N" aria-label */
                    aria-label={`Remove invitation ${index + 1}`}
                    variant="danger"
                    onClick={() => remove(index)}
                  />
                )}
              </div>
            ))}
          </div>

          {errors.invitations && typeof errors.invitations.message === 'string' && (
            <p className="text-xs text-danger mt-2">{errors.invitations.message}</p>
          )}
          <p className="text-xs text-ink-muted mt-2">
            {t('connections.invite.maxHelp')}
          </p>
        </div>

        <FormField label={t('connections.invite.messageLabel')} error={errors.message?.message}>
          <Textarea
            rows={3}
            placeholder={t('connections.invite.messagePlaceholder')}
            maxLength={500}
            {...register('message')}
          />
        </FormField>

        <div className="flex items-center gap-2 pt-2">
          <Button type="submit" loading={isLoading} leadingIcon={HiOutlineUsers} className="flex-1">
            {t('connections.invite.submit')}
          </Button>
          <Button type="button" variant="secondary" onClick={() => reset()} disabled={isLoading}>
            {t('connections.invite.clear')}
          </Button>
        </div>
      </form>

      <Banner tone="info" title={t('connections.invite.howWorksTitle')}>
        <ul className="text-sm space-y-1 list-disc list-inside ms-1">
          <li>{t('connections.invite.howWorks1')}</li>
          <li>{t('connections.invite.howWorks2')}</li>
          <li>{t('connections.invite.howWorks3', { entity: employeeTerm.plural })}</li>
          <li>{t('connections.invite.howWorks4', { conn: connectionTerm.plural })}</li>
        </ul>
      </Banner>
    </div>
  )
}
